/**
 * Spec content data model — blocks, modules, variants, masters, drafts,
 * project specs, and the assembled-spec / diff types used during compile
 * and review.
 *
 * Split from src/shared/types.ts on 2026-04-19. types.ts re-exports from
 * here so existing `import { ... } from '../shared/types'` keeps working.
 */

import type { EditHistoryEntry } from './entities';

// ─── Block primitives ───

// ─── Table borders (Word-style, TABLE_BORDERS_AND_RENAME_PLAN T1.1) ───

/** The five line styles a border can have, plus no line. */
export type BorderLineStyle = 'solid' | 'double' | 'dashed' | 'dotted' | 'none';

/** One border line. `width` is points (Word's own unit, ×8 = `w:sz`);
 *  style 'none' always carries width 0. Colour is NOT part of the line — it
 *  keeps coming from the table's legacy colour fields (no colour UI, D1). */
export interface BorderLine {
  style: BorderLineStyle;
  width: number;
}

/** The six lines a table itself owns (Word's `w:tblBorders`). */
export type TableEdge = 'top' | 'bottom' | 'left' | 'right' | 'insideH' | 'insideV';
export const TABLE_EDGES: readonly TableEdge[] = [
  'top',
  'bottom',
  'left',
  'right',
  'insideH',
  'insideV',
];

/** One side of one cell: `false` = no line, `true` = the table's own inside
 *  line explicitly, a `BorderLine` = exactly that line. */
export type CellBorderValue = boolean | BorderLine;

/** Per-cell border overrides (Phase 1 item 3, 2026-08-25; widened to
 *  `BorderLine` by T1.1, 2026-09-24).
 *
 *  A side that is ABSENT inherits the table's own lines (`TableData.edges`,
 *  else the legacy `border*` / `innerBorder*`) — exactly what every cell did
 *  before this field existed, so a table authored without touching cell
 *  borders round-trips and prints byte-identically. */
export interface TableCellBorders {
  top?: CellBorderValue;
  right?: CellBorderValue;
  bottom?: CellBorderValue;
  left?: CellBorderValue;
}

/** Is this a well-formed BorderLine (not a boolean, not junk)? */
export function isBorderLine(v: unknown): v is BorderLine {
  if (!v || typeof v !== 'object') return false;
  const l = v as Partial<BorderLine>;
  return (
    (l.style === 'solid' ||
      l.style === 'double' ||
      l.style === 'dashed' ||
      l.style === 'dotted' ||
      l.style === 'none') &&
    typeof l.width === 'number' &&
    Number.isFinite(l.width)
  );
}

/** The fields a border resolver reads — satisfied by both `TableData` and the
 *  Designer's `tableStyle` attr. */
export interface TableBorderFields {
  edges?: Partial<Record<TableEdge, BorderLine>>;
  borderWidth?: number;
  borderColor?: string;
  borderStyle?: string;
  innerBorderWidth?: number;
  innerBorderColor?: string;
  innerBorderStyle?: string;
}

/** A line with the colour it prints in. */
export interface ResolvedBorder extends BorderLine {
  color: string;
}

/** Legacy defaults — the outer 1 pt black / inner ½ pt grey grid every table
 *  has always drawn and exported when a field is absent. Document data (what
 *  Word prints), not UI chrome. */
const LEGACY_OUTER = { width: 1, style: 'solid', color: '000000' } as const;
const LEGACY_INNER = { width: 0.5, style: 'solid', color: '999999' } as const;

function asLineStyle(s: string | undefined, fallback: BorderLineStyle): BorderLineStyle {
  return s === 'solid' || s === 'double' || s === 'dashed' || s === 'dotted' || s === 'none'
    ? s
    : fallback;
}

/** One table edge, resolved: `edges[edge]` when set, else the legacy fields
 *  (`border*` for the four outer edges, `innerBorder*` for insideH/insideV),
 *  else the legacy defaults. Colour always comes from the legacy colour
 *  field of that half (outer / inner). */
export function resolveTableEdge(t: TableBorderFields, edge: TableEdge): ResolvedBorder {
  const inner = edge === 'insideH' || edge === 'insideV';
  const color = (
    (inner ? t.innerBorderColor : t.borderColor) || (inner ? LEGACY_INNER : LEGACY_OUTER).color
  ).replace('#', '');
  const own = t.edges?.[edge];
  if (own && isBorderLine(own)) return { style: own.style, width: own.width, color };
  const legacy = inner ? LEGACY_INNER : LEGACY_OUTER;
  return {
    style: asLineStyle(inner ? t.innerBorderStyle : t.borderStyle, legacy.style),
    width: (inner ? t.innerBorderWidth : t.borderWidth) ?? legacy.width,
    color,
  };
}

export type CellSide = 'top' | 'right' | 'bottom' | 'left';

/** One side of one cell, resolved: cell override → table edge → legacy.
 *  `onOuterEdge` = this side lies on the table's own outer edge (so the
 *  table's top/bottom/left/right line applies, not an inside line).
 *   · absent   → the table's line for where the side sits
 *   · `false`  → no line
 *   · `true`   → the table's INSIDE line (insideH for top/bottom, insideV for
 *                left/right) — what `true` has always meant
 *   · BorderLine → that line, in the colour of the line it replaces */
export function resolveCellSide(
  t: TableBorderFields,
  side: CellSide,
  onOuterEdge: boolean,
  override: CellBorderValue | undefined
): ResolvedBorder {
  const insideEdge: TableEdge = side === 'top' || side === 'bottom' ? 'insideH' : 'insideV';
  const here = resolveTableEdge(t, onOuterEdge ? side : insideEdge);
  if (override === undefined) return here;
  if (override === false) return { style: 'none', width: 0, color: here.color };
  if (override === true) return resolveTableEdge(t, insideEdge);
  return { style: override.style, width: override.width, color: here.color };
}

export interface TableCell {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  /** Manual text highlight (the marker pen) — same meaning as
   *  ContentBlock.highlight, added 2026-08 (the Designer's R7 table rebuild)
   *  so a cell's marker-pen use survives a Designer round trip. */
  highlight?: boolean;
  /** Mixed inline emphasis inside one cell (the Designer's R7 table rebuild,
   *  2026-08). Same semantics as ContentBlock.runs: absent ⇒ the whole cell
   *  uses the flags above; a run's own flag COMPOSES with them. Concatenating
   *  every `text` in order always reproduces `TableCell.text` exactly. */
  runs?: TextRunSpan[];
  bgColor?: string;
  bgOpacity?: number;
  /** Per-side border overrides. Absent ⇒ the cell uses the table's grid. */
  borders?: TableCellBorders;
  align?: 'left' | 'center' | 'right';
  /** Vertical alignment of the cell's content inside its own box (Word's
   *  `w:vAlign`). Absent ⇒ top — exactly what every cell did before this
   *  field existed, so an untouched table's .docx is byte-identical. */
  valign?: 'top' | 'middle' | 'bottom';
  colSpan?: number;
  rowSpan?: number;
}

export interface TableData {
  rows: number;
  cols: number;
  cells: TableCell[][];
  colWidths?: number[]; // percentages, should sum to 100
  /** EXPLICIT "AutoFit contents": every column sizes to what is in it, and
   *  the .docx carries no per-cell widths at all. Never inferred from a
   *  missing `colWidths` — a legacy or imported table without widths keeps
   *  the even fixed columns it has always printed. */
  autofit?: boolean;
  /** The table's own width as a percentage of the text column (Word's
   *  `w:tblW` in pct). Absent ⇒ 100, full width, which is what every table
   *  printed before this field existed. */
  width?: number;
  /** Padding inside every cell, in points, all four sides (Word's
   *  table-level `w:tblCellMar`). Absent ⇒ Word's own default margins. */
  cellPadding?: number;
  /** MINIMUM height per row, in points, one entry per row (Word's
   *  `w:trHeight` with `atLeast`). Sparse: `null` — or the whole field
   *  absent — means the row sizes to its content, which is what every row
   *  did before this field existed. */
  rowHeights?: (number | null)[];
  /** Per-edge table lines (Word's `w:tblBorders`, T1.1). An absent edge falls
   *  back to the legacy fields below (`border*` for the four outer edges,
   *  `innerBorder*` for insideH/insideV) — so a table that never touched the
   *  Borders controls carries no `edges` and serialises byte-identically. */
  edges?: Partial<Record<TableEdge, BorderLine>>;
  borderWidth?: number; // pt (0 = no border)
  borderColor?: string; // hex
  borderStyle?: 'solid' | 'dashed' | 'dotted' | 'double';
  innerBorderWidth?: number; // pt for inner grid lines
  innerBorderColor?: string;
  innerBorderStyle?: 'solid' | 'dashed' | 'dotted' | 'none';
  headerRows?: number; // number of header rows (bold bg, repeats on every printed page)
  headerBgColor?: string;
  headerBgOpacity?: number;
  tableJustify?: 'left' | 'center' | 'right';
  /** R7 (2026-08, the Designer): does this table carry a MasterFormat level
   *  number, like a numbered paragraph, or sit standalone between paragraphs
   *  with no number? Author's choice, switchable after insertion. Default
   *  (absent/false) = standalone — matches every table that existed before
   *  this field, which never carried a number in the .docx either. */
  numbered?: boolean;
}

/** One styled span of text inside a paragraph.
 *
 *  ADDITIVE (2026-08-10, the Designer): a paragraph whose emphasis is uniform
 *  keeps using the block-level `bold`/`italic`/`underline` flags and carries
 *  no `runs`. A paragraph with MIXED emphasis stores the split here.
 *  Concatenating every `text` in order always reproduces `ContentBlock.text`
 *  exactly, so every existing consumer (diff, search, masters, smart blocks)
 *  keeps reading `.text` and is unaffected. */
export interface TextRunSpan {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  /** Manual text highlight (the marker pen). Distinct from revision marks —
   *  those are computed at render/export time and never stored. */
  highlight?: boolean;
}

export interface ContentBlock {
  id: string;
  level: number;
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  /** Manual text highlight applied to the whole paragraph. */
  highlight?: boolean;
  /** Mixed inline emphasis. Absent ⇒ the whole paragraph uses the block-level
   *  bold/italic/underline/highlight flags. Emphasis on a run COMPOSES with
   *  those flags. */
  runs?: TextRunSpan[];
  tags?: string[];
  type?: 'fixed' | 'editable' | 'optional';
  confidence?: string;
  source?: string;
  systemName?: string;
  systemId?: string;
  included?: boolean;
  blockType?: 'text' | 'table' | 'image' | 'smart-token';
  tableData?: TableData;
  /** Base64-encoded image data URI (data:image/xxx;base64,...) */
  imageData?: string;
  /** Image display width as percentage (20-100). Default: 100 */
  imageWidth?: number;
  // ─── Smart Token fields (blockType === 'smart-token') ───
  /** Which firm-scoped SmartBlock entity this instance references.
   *  Editing the SmartBlock updates every instance at next compile/render. */
  smartBlockId?: string;
  /** Cached kind id (e.g. 'related-sections', 'project-identity', 'custom').
   *  Allows the editor chip to render even when SmartBlock fetch is in flight
   *  or the block is offline. Authoritative copy lives on the SmartBlock row. */
  smartBlockKind?: string;
  /** Per-instance parameter overrides — e.g. divisionFilter, excludeIds[].
   *  Merged on top of the SmartBlock's default params at resolve time.
   *  Related sections reads its typed view through `readRelatedParams`
   *  (shared/related-sections.ts) — seeded / includeIds / knownIds. */
  smartParams?: Record<string, any>;
  /** When true, the block is skipped at export time. Stays visible in the editor
   *  (dimmed + strikethrough) so it can be toggled back on. Applies to both
   *  master spec editing and project spec editing. */
  excluded?: boolean;
  /** When true, this block is a continuation of the previous block — it shares
   *  the same paragraph number and renders without its own prefix. */
  continuation?: boolean;
  /** GROUP membership. Every block carrying the same value is ONE group: kept
   *  contiguous in document order, and selected, moved, copied, hidden and
   *  deleted as a unit. One level only — a block belongs to at most one group.
   *
   *  It is the HOST DOCUMENT's structure, never content. That is why it sits
   *  outside `CONTENT_KEYS` (so grouping a block can never read as a content
   *  edit — module drift, publish diffs and staleness all stay honest) and
   *  outside a module definition (`RUNTIME_KEYS` in spec-modules.ts strips it
   *  on capture). A client layer CAN still express one, through
   *  `OVERRIDE_KEYS` — see spec-compose.ts. */
  groupId?: string;
  /** Revision info computed at render/export time by diffing against the
   *  latest issuance snapshot. Transient — never persisted on saved blocks. */
  revision?: BlockRevision;
  /** Comments/notes on this block for review collaboration. */
  comments?: BlockComment[];
  /** Set on every block that arrived by INSERTING a module (Phase 5). It is
   *  identity, never a link: the blocks are a stamped COPY, and nothing ever
   *  resolves `moduleId` back to the library to re-read content (v2.12 grave).
   *  Phase 6's update PRs read `moduleVersion`, so it must survive every save.
   *  Typed by reference so `spec-modules.ts` stays the one place that owns the
   *  module vocabulary. */
  moduleStamp?: import('./spec-modules').ModuleStamp;

  // ─── DEMOLISHED (Phase 2, 2026-08-25) ───
  // `isVariant` / `variantId` / `variantIds` / `excludedFromVariants` (legacy
  // per-variant stamping) and `slotId` / `optionId` (Masters Ladder choice
  // points) are GONE. A block is a block; per-client authorship lives in a
  // ClientLayer, nowhere else. `spec-compose.ts` still SCRUBS those six keys
  // off stored rows on the way to an export, so data written before the
  // materializer ran can never leak a dead stamp into a .docx.
}

export interface BlockComment {
  id: string;
  text: string;
  author: string;
  createdAt: string;
  resolved?: boolean;
}

export interface SystemModule {
  id: string;
  name: string;
  icon?: string;
  content: ContentBlock[];
}

// ─── Variants ───

/** A named configuration of a section (e.g., "Verkada", "Axis") */
export interface Variant {
  id: string;
  name: string;
  description?: string;
  clientOrgId?: string; // Client/org associated with this variant
  tags?: string[];
  version: number;
  status: SpecMasterStatus;
  lastModified: string;
  modifiedBy?: string;
  versionHistory?: VariantVersion[];
}

/** Point-in-time snapshot of blocks visible in a variant */
export interface VariantVersion {
  version: number;
  snapshot: ContentBlock[];
  savedBy: string;
  savedAt: string;
  message?: string;
}

// ═══════════════════════════════════════════════════════════════
//  Masters Ladder — Layers (R1, 2026-08-23)
//
//  ONE axis survives Phase 2's demolition:
//
//    Layers = the pen — per-client authorship stacked on core
//             (adds / overrides / hides).
//
//  The Selections axis (slots / options / picks / presets — the "switch")
//  was demolished 2026-08-25 per docs/MASTERS_UNIFIED_PLAN.md Phase 2.
//  Composition order lives in ONE place: `src/shared/spec-compose.ts`.
// ═══════════════════════════════════════════════════════════════

/** A block added by a client layer, anchored into the composed document.
 *  `afterBlockId` is the anchor; `index` is the fallback used only when the
 *  anchor block is missing from the composition (deleted upstream). */
export interface LayerAdd {
  block: ContentBlock;
  afterBlockId?: string;
  index?: number;
  /** Set (to the update's changeId) while this add is the HOLD for a core block
   *  the master removed: it keeps reading in the variant until the update is
   *  answered. ACCEPT deletes the add; REJECT clears this marker so it becomes
   *  an ordinary variant add. */
  heldFromRemoval?: string;
}

/** The subset of a block a client override replaces. Merged OVER the base
 *  block — absent keys keep the base value. */
export type BlockOverridePayload = Partial<Omit<ContentBlock, 'id'>>;

/** Snapshot of the base block at the moment the override was written, so the
 *  Studio can say "core has changed since you wrote this" (R3). */
export interface BlockBaseSnapshot {
  /** Content hash of the base block — see `hashBlock()` in spec-compose. */
  hash: string;
  /** The base block's content fields, for the side-by-side compare. */
  fields?: BlockOverridePayload;
  capturedAt?: string;
}

/** A client's edit of a block. */
export interface BlockOverride {
  id: string;
  /** Which block this shadows. */
  blockId: string;
  payload: BlockOverridePayload;
  base?: BlockBaseSnapshot;
  createdAt?: string;
  updatedAt?: string;
  updatedBy?: string;
}

/** One client's authorship stacked on the office master. Hides never delete —
 *  composed output exposes them as ghosts so editor surfaces can offer
 *  "restore". */
export interface ClientLayer {
  id: string;
  /** Legacy link to the `clients` table (migration 044). Cohesion R1 Phase 3:
   *  variants are freeform-named, not client-linked — new variants never set
   *  this. Kept, additive-only, so an old layer created before that ruling
   *  still round-trips. */
  clientId?: string;
  /** Display name of the variant — its identity. Every variant created by the
   *  app sets this at mint time; back-compat only falls through to a client
   *  lookup by `clientId` for a pre-name-field row (see `useVariant`). */
  name?: string;
  /** Which master this layer belongs to. Redundant when the layer is stored
   *  on the master row, but kept so a layer survives being moved. */
  masterId?: string;
  adds?: LayerAdd[];
  overrides?: BlockOverride[];
  hides?: string[];
  createdAt?: string;
  updatedAt?: string;
  updatedBy?: string;
}

/** A spec-ladder client — the rung between the firm's office master and a
 *  project. Distinct from `Organization`: this is the entity a layer and its
 *  picks hang off. Persisted in the `clients` table (migration 044). */
export interface SpecClient {
  id: string;
  name: string;
  /** Optional link to an existing organization row. */
  orgId?: string;
  archivedAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

export type SpecMasterStatus = 'draft' | 'pending_review' | 'published' | 'archived';
/** @deprecated Use SpecMasterStatus */
export type BlueprintStatus = SpecMasterStatus;

export interface SpecMasterVersion {
  version: number;
  snapshot: {
    coreContent: ContentBlock[];
    systems: SystemModule[];
    title?: string;
    description?: string;
  };
  savedBy: string;
  savedAt: string;
  message?: string;
  /** What archived this version, when it was not an ordinary publish.
   *  'module-update' = a module update accepted onto the master's core
   *  content (the server archives before it rewrites — rulings 2026-08-26). */
  source?: 'module-update';
  /** The feed entry being answered when this snapshot was taken. The server
   *  reads it back to diff the master's PRE-answer core against the answered
   *  one, so the variant cascade describes the whole answer even when its ops
   *  were decided one at a time. */
  sourceEntryId?: string;
}
/** @deprecated Use SpecMasterVersion */
export type BlueprintVersion = SpecMasterVersion;

export interface PublishRequest {
  id: string;
  draftId: string;
  blueprintId: string; // specMasterId — kept as blueprintId for serialization compat
  requestedBy: string;
  requestedAt: string;
  message?: string;
  status: 'pending' | 'approved' | 'rejected';
  reviewedBy?: string;
  reviewedAt?: string;
  reviewNote?: string;
}

/** The "main branch" — published source of truth. Never edited directly. */
export interface SpecMaster {
  id: string;
  name?: string;
  csiNumber?: string;
  title?: string;
  description?: string;
  division?: string;
  sectionNumber?: string;
  status?: SpecMasterStatus;
  version: number;
  modifiedBy?: string;
  coreContent: ContentBlock[];
  systems: SystemModule[]; // DEPRECATED — kept for backward compat
  variants?: Variant[]; // named variant configurations
  /** Masters Ladder (R1) — per-client authorship stacked on core. */
  layers?: ClientLayer[];
  lastModified: string;
  tags?: string[];
  versionHistory?: SpecMasterVersion[];
  maxVersionsKept?: number; // default 10
  // Edit locking
  lockedBy?: string; // userId or userName of who checked it out
  lockedAt?: string; // when the lock was acquired
  editHistory?: EditHistoryEntry[];
  // Spec metadata — ownership and responsibility
  owner?: string; // principal/engineer responsible for this spec
  creator?: string; // who originally created it
  editor?: string; // who typically edits / maintains it
  notes?: string; // internal notes about this spec
  archivedAt?: string | null;
  archivedBy?: string | null;
}
/** @deprecated Use SpecMaster */
export type Blueprint = SpecMaster;

/** A user's working copy — like a git branch. Saved locally, not published. */
export interface SpecMasterDraft {
  id: string; // unique draft id
  blueprintId: string; // which published spec master this forks from (kept for serialization compat)
  baseVersion: number; // version of published spec master when forked
  editedBy: string;
  csiNumber?: string;
  title?: string;
  description?: string;
  coreContent: ContentBlock[];
  systems: SystemModule[];
  variants?: Variant[];
  /** Masters Ladder (R1) — see SpecMaster.layers. */
  layers?: ClientLayer[];
  activeVariantId?: string; // which variant is being edited (null = core/all view)
  createdAt: string;
  lastSaved: string;
  publishRequest?: PublishRequest;
}
/** @deprecated Use SpecMasterDraft */
export type BlueprintDraft = SpecMasterDraft;

/** Diff entry for publish review */
export interface BlockDiff {
  blockId: string;
  level: number;
  type: 'added' | 'removed' | 'modified' | 'unchanged';
  oldText?: string;
  newText?: string;
  oldLevel?: number;
  newLevel?: number;
}

// ─── Assembled Spec (Compile) ───

export interface AssembledSpec {
  master: SpecMaster;
  blocks: (ContentBlock & { source: string; included: boolean })[];
  selections: {
    enabledSystems: string[];
    options: Record<string, any>;
    parameters: Record<string, any>;
  };
}

// ─── Project Specs (per-project fork of a spec master) ───

/** Full frozen copy of a project spec's renderable state, captured on the
 *  Issuance record at the moment it is issued. The change-tracking engine
 *  diffs current content against the latest snapshot — computed on demand,
 *  never stamped per-edit, so it cannot drift or miss an edit path. */
export interface IssuanceSnapshot {
  coreContent: ContentBlock[];
  systems: SystemModule[];
  tokenOverrides?: Record<string, string>;
  formaId?: string;
  csiNumber?: string;
  title?: string;
}

/** Issuance = project-side snapshot of a project spec (IFC, Addendum 1, ASI 20…).
 *  Completely separate from master-spec Variants. Track document releases per project. */
export interface Issuance {
  id: string;
  name: string; // e.g. "IFC", "Addendum 1", "ASI 20"
  date?: string; // ISO date of issuance
  notes?: string;
  createdAt?: string;
  /** Display name of who issued it. */
  issuedBy?: string;
  /** Frozen copy at issue time. Absent on legacy issuances created before
   *  snapshots existed — those can't be viewed/reverted/diffed against. */
  snapshot?: IssuanceSnapshot;
  /** Set when this issuance was created by reverting to an earlier one —
   *  points at the issuance whose snapshot was restored. */
  revertedFromId?: string;
  /** 'auto' = a quiet rolling design-phase safety snapshot (the Designer's
   *  recoverable history). Auto rows are capped, hidden from the issuance
   *  timeline, and NEVER a revision-mark baseline. Undefined = a real
   *  issuance (IFC, Addendum 1…). */
  kind?: 'auto';
}

/** The issuances that count as ISSUANCES — auto safety snapshots excluded.
 *  Every consumer that reads "the latest issuance" (token resolution,
 *  revision baselines, history dropdowns) must read through this filter. */
export function realIssuances(list: Issuance[] | undefined | null): Issuance[] {
  return (list || []).filter((i) => i.kind !== 'auto');
}

// ─── Revision marks (project specs, post-issuance change tracking) ───

/** One diffed run of text inside a modified block. */
export interface RevisionToken {
  value: string;
  op: 'keep' | 'add' | 'del';
}

/** Render/export-time revision info attached to a block by the diff engine.
 *  Computed against the latest issuance snapshot — NEVER persisted. */
export interface BlockRevision {
  kind: 'added' | 'removed' | 'modified';
  wordDiff?: RevisionToken[];
}

/** How one category of change renders — in the editor's paper AND the Word
 *  export. Highlight values map 1:1 onto Word's highlighter palette. */
export interface RevisionMarkStyle {
  strikethrough?: boolean;
  underline?: 'none' | 'single' | 'double';
  highlight?: 'none' | 'yellow' | 'green' | 'cyan' | 'magenta' | 'gray';
  /** Text color, hex without '#'. Empty/undefined = inherit body color. */
  color?: string;
  bold?: boolean;
}

/** The architect's dictated revision-marking style for a project spec.
 *  Presentation only — WHAT changed is always computed from the latest
 *  issuance snapshot; these settings only control how changes LOOK. */
export interface RevisionSettings {
  /** Master switch: render marks on the editor's formatted paper. */
  showMarks: boolean;
  added: RevisionMarkStyle;
  deleted: RevisionMarkStyle;
  /** Vertical change bar in the margin beside any changed paragraph. */
  changeBars?: boolean;
}

export const DEFAULT_REVISION_SETTINGS: RevisionSettings = {
  showMarks: true,
  added: { underline: 'single' },
  deleted: { strikethrough: true },
  changeBars: true,
};

export interface ProjectSpec {
  id: string;
  projectId: string;
  masterId: string; // which spec master this was forked from
  masterVersion: number; // version of the master at time of compile
  /** Sync D1 — the exact blocks this spec was forked with (module stamps
   *  intact), the common ancestor for the three-way merge. Column `sync_base`. */
  syncBase?: ContentBlock[];
  variantId?: string; // which variant was selected (if any)
  formaId?: string; // which forma was assigned
  /** Live token values picked in the rendered view (e.g. {Deliverable}).
      Merged over project data at render + export. Keys are token paths
      like 'Deliverable' or 'Issuance.Name'. */
  tokenOverrides?: Record<string, string>;
  csiNumber?: string;
  title?: string;
  description?: string;
  coreContent: ContentBlock[];
  systems: SystemModule[];
  variants?: Variant[];
  /** Project-side issuances (IFC, Addendum 1, ASI 20, etc). */
  issuances?: Issuance[];
  /** Architect-dictated revision-mark styling for this spec's post-issuance
   *  edits. Undefined = DEFAULT_REVISION_SETTINGS. */
  revisionSettings?: RevisionSettings;
  compiledAt: string; // when this was first compiled into the project
  lastEdited?: string; // last time project-level edits were made
  editedBy?: string;
  archivedAt?: string | null;
  archivedBy?: string | null;
  /** SYNC Publish gate (migration 061). Written ONLY by the server's
   *  `project_spec_publish` action — exports read these, never the draft. */
  publishedContent?: ContentBlock[] | null;
  publishedSystems?: SystemModule[] | null;
  publishedVersion?: number;
  publishedAt?: string | null;
  publishedBy?: string | null;
}

// ─── Manual-level issuances (the Issuance Desk's records) ───
// One project = one Manual. A manual issuance (IFC, Addendum 1, ASI 3…)
// freezes a per-section Issuance snapshot on every included spec and records
// WHICH sections were included and which changed. The per-section snapshots
// stay where they always lived — inside each ProjectSpec's `issuances` array;
// the manifest references them by id. (migration 036)

/** One section's line in a manual issuance's manifest. */
export interface ManualIssuanceManifestEntry {
  /** The ProjectSpec included in this issuance. */
  specId: string;
  /** The per-section Issuance (with snapshot) this issuance froze for it. */
  specIssuanceId: string;
  /** True when the section's content differed from the previous manual
   *  issuance's snapshot (or the section was added after it). */
  changed: boolean;
}

/** A manual-level issuance record — the Procore-drawings model. */
export interface ProjectManualIssuance {
  id: string;
  projectId: string;
  name: string; // e.g. "IFC", "Addendum 1"
  date?: string; // ISO date of issuance
  notes?: string;
  issuedBy?: string;
  manifest: ManualIssuanceManifestEntry[];
  createdAt?: string;
}

// ─── Smart Blocks ───
// Dynamic, context-aware spec content. Author drops one into a master spec;
// at project-compile/render time the resolver registry computes the actual
// ContentBlock[] from the project context (other specs in project, project
// metadata, team, issuance, etc.). Conceptually a Word field / cross-reference
// for spec prose. Same draft/publish lifecycle as SpecMaster — firm members
// can draft, publishers approve.

/** Built-in resolver identifiers. The `custom` kind is data-driven via
 *  querySpec + templateSpec on the SmartBlock row. */
export type SmartBlockKind =
  | 'related-sections'
  | 'section-header'
  | 'project-identity'
  | 'issue-date'
  | 'standards-referenced'
  | 'engineer-of-record'
  | 'custom';

/** Parameter type contract for a smart block. The Creator UI renders an
 *  input control per `kind` and writes back into ContentBlock.smartParams. */
export type SmartBlockParamType =
  | 'string'
  | 'number'
  | 'boolean'
  | 'enum' // requires `options[]`
  | 'multiselect' // requires `options[]`
  | 'division' // CSI division code, e.g. "27"
  | 'spec-ref-list'; // multi-pick from project's spec list at resolve time

export interface SmartBlockParam {
  key: string;
  label: string;
  description?: string;
  type: SmartBlockParamType;
  required?: boolean;
  default?: any;
  options?: { value: string; label: string }[]; // for enum / multiselect
  placeholder?: string;
}

/** Declarative source description for the `custom` kind. Resolves at compile
 *  time against ProjectContext. Intentionally small — not a SQL surface. */
export interface SmartBlockQuerySpec {
  /** What to pull from project context. */
  from:
    | 'project-specs' // every other spec in the project
    | 'team-members'
    | 'project-fields' // single record from project itself
    | 'issuances'
    | 'specs-in-section'; // text scan of THIS spec
  /** Filter clauses. Empty = no filter. AND-combined. */
  where?: SmartBlockQueryClause[];
  /** Field to sort by. */
  sort?: string;
  /** Asc or desc. Default asc. */
  sortDir?: 'asc' | 'desc';
  /** Optional cap. */
  limit?: number;
  /** Visual "Build" composer design. IGNORED by the resolver (which renders
   *  templateSpec) — these exist so the no-code composer can generate the
   *  template AND re-hydrate itself on re-open. Absent on hand-authored
   *  Advanced blocks. */
  layout?: 'bulleted' | 'labeled' | 'inline' | 'table';
  /** Fields to display, in order, for the chosen layout. */
  fields?: string[];
  /** Per-field display labels for the 'labeled' layout. */
  labels?: Record<string, string>;
}

export interface SmartBlockQueryClause {
  field: string; // e.g. 'division', 'role', 'projectField:phase'
  op: 'equals' | 'not-equals' | 'contains' | 'in' | 'not-in' | 'starts-with' | 'regex';
  /** Static value OR a `$param` reference resolved from instance params. */
  value: any;
}

/** Mustache-ish template spec for the `custom` kind. Rendered with the result
 *  of the query plus param values. Supported helpers: `{{#each}}`, `{{field}}`,
 *  `{{#if condition}}`, `{{join items "sep"}}`. */
export type SmartBlockTemplateSpec = string;

/** A firm-scoped Smart Block definition. */
export interface SmartBlock {
  id: string;
  firmId?: string;
  name: string;
  description?: string;
  kind: SmartBlockKind;
  /** Lucide icon name (e.g. 'Link', 'Hash'). Default per kind in registry. */
  icon?: string;
  /** Hex or token name for chip accent. Default per kind in registry. */
  color?: string;
  category?: string;
  tags?: string[];
  /** Declared inputs the author sees when inserting this block in a spec. */
  paramSchema?: SmartBlockParam[];
  /** Default param values for new instances. */
  defaultParams?: Record<string, any>;
  /** ONLY for kind === 'custom'. Built-in kinds ignore these and use their
   *  code-defined resolver. */
  querySpec?: SmartBlockQuerySpec;
  /** ONLY for kind === 'custom'. */
  templateSpec?: SmartBlockTemplateSpec;
  status?: SpecMasterStatus;
  version: number;
  versionHistory?: SmartBlockVersion[];
  maxVersionsKept?: number;
  lockedBy?: string;
  lockedAt?: string;
  owner?: string;
  creator?: string;
  modifiedBy?: string;
  editHistory?: EditHistoryEntry[];
  lastModified: string;
  createdAt?: string;
  archivedAt?: string | null;
  archivedBy?: string | null;
}

export interface SmartBlockVersion {
  version: number;
  snapshot: {
    name: string;
    kind: SmartBlockKind;
    paramSchema?: SmartBlockParam[];
    defaultParams?: Record<string, any>;
    querySpec?: SmartBlockQuerySpec;
    templateSpec?: SmartBlockTemplateSpec;
    icon?: string;
    color?: string;
    category?: string;
  };
  savedBy: string;
  savedAt: string;
  message?: string;
}

export interface SmartBlockDraft {
  id: string;
  smartBlockId: string;
  baseVersion?: number;
  editedBy: string;
  name?: string;
  description?: string;
  kind?: SmartBlockKind;
  icon?: string;
  color?: string;
  category?: string;
  tags?: string[];
  paramSchema?: SmartBlockParam[];
  defaultParams?: Record<string, any>;
  querySpec?: SmartBlockQuerySpec;
  templateSpec?: SmartBlockTemplateSpec;
  publishRequest?: PublishRequest;
  createdAt: string;
  lastSaved: string;
}

/** What a resolver receives at project-compile / render time. Built fresh
 *  per spec by the compile pipeline. Resolvers are pure functions —
 *  same context + params always produce the same ContentBlock[]. */
export interface ProjectContext {
  project: {
    id: string;
    name?: string;
    number?: string;
    address?: string;
    phase?: string;
    [k: string]: any;
  };
  thisSpec: {
    id: string;
    masterId?: string;
    sectionNumber?: string;
    csiNumber?: string;
    title?: string;
    division?: string;
    coreContent: ContentBlock[];
  };
  /** All OTHER specs in this project — does NOT include `thisSpec`. */
  otherSpecsInProject: Array<{
    id: string;
    sectionNumber?: string;
    csiNumber?: string;
    title?: string;
    division?: string;
  }>;
  /** Ids of this project's ARCHIVED specs. Kept apart from
   *  `otherSpecsInProject` (which still lists them, unchanged for every
   *  legacy kind) so Related sections can leave them out of the book. */
  archivedSpecIds?: string[];
  team: Array<{
    id: string;
    name?: string;
    email?: string;
    role?: string;
    title?: string;
  }>;
  client?: { id: string; name?: string; address?: string };
  currentIssuance?: Issuance;
  /** Time the compile is happening — used for default Issue Date. */
  resolvedAt: string;
}

/** Resolver signature: pure function that returns ContentBlocks to splice in. */
export type SmartBlockResolver = (
  params: Record<string, any>,
  context: ProjectContext,
  smartBlock?: SmartBlock
) => ContentBlock[];

/** Working-copy of a ProjectSpec — same git-like workflow as SpecMasterDraft.
 *  User checks out → edits core content / modules / variants → submits for
 *  review → a publisher-or-above approves → approval copies the draft back
 *  onto the ProjectSpec and clears the draft. */
export interface ProjectSpecDraft {
  id: string; // unique draft id
  projectSpecId: string; // which project spec this forks from
  baseIssuanceId?: string; // which issuance was current when the draft was forked
  editedBy: string;
  csiNumber?: string;
  title?: string;
  description?: string;
  coreContent: ContentBlock[];
  variants?: Variant[];
  activeVariantId?: string;
  publishRequest?: PublishRequest;
  createdAt: string;
  lastSaved: string;
}
